import axios, { AxiosInstance } from 'axios';
import { App } from '../App';

/**
 * The `Api` class simplifies calls to Axios. It offers Get, Post, Put,
 * Delete and Upload methods, which can be **typed**.
 *
 * `Api` uses an instance of Axios that is configured to send along
 * an XSRF token, as well as cookie credentials.
 *
 * @example
 * Api.Get<ISystem>("systems/32")
 * .then(res => console.log(res));
 */
class Api {

  //
  // Laravel:
  // You should enable the withCredentials and withXSRFToken options
  // on your application's global axios instance.
  //
  // The Axios instance is a singleton.
  //
  private static _axios: AxiosInstance | null = null;
  private static accessToken: string | null = null;

  static getAccessToken(): string | null {
    return Api.accessToken;
  }

  static setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  static axios = (): AxiosInstance => {
    if(this._axios) return this._axios;
    this._axios = axios.create({
      withCredentials: true,
      withXSRFToken: true,
      timeout: 30000,
      headers: {
        Accept: "application/json",
      },
    });
    // This request interceptor adds an authorization header.
    this._axios.interceptors.request.use((request) => {
      if (Api.accessToken) {
        request.headers.set('Authorization', `Bearer ${Api.accessToken}`);
      }
      return request;
    });
    return this._axios;
  }

  /**
   * Perform a GET request of type T at given URL.
   *
   * @param url URL to retrieve
   * @param signal Optional abort signal
   * @returns Promise
   */
  static async Get<T> (url: string, signal?: AbortSignal): Promise<T> {
    const response = await this.axios().get<T>(App.config.apiURL + url, {
      signal: signal
    })
    return response.data;
  }

  /**
   * Perform a POST request of type T at given URL.
   *
   * @param url URL to access
   * @param request Object with POST information
   * @param signal Optional abort signal
   * @returns Promise
   */
  static async Post<T>(url: string, request: any, signal?: AbortSignal): Promise<T> {
    const response = await this.axios().post<T>(App.config.apiURL + url, request, {
      signal: signal
    });
    return response.data;
  }

  /**
   * Perform a PUT request of type T at given URL.
   *
   * @param url URL to access
   * @param request Object with PUT information
   * @param signal Optional abort signal
   * @returns Promise
   */
  static async Put<T>(url: string, request: any, signal?: AbortSignal): Promise<T> {
    const response = await this.axios().put<T>(App.config.apiURL + url, request, {
      signal: signal
    });
    return response.data;
  }

  /**
   * Perform a DELETE request of type T at given URL.
   *
   * @param url URL to access
   * @param signal Optional abort signal
   * @returns Promise
   */
  static async Delete<T>(url: string, signal?: AbortSignal): Promise<T> {
    const response = await this.axios().delete<T>(App.config.apiURL + url, {
      signal: signal
    });
    return response.data;
  }

  /**
   * Perform a POST request of type T at given URL. This request sends
   * `FormData`, and sets the form-data header.
   *
   * @param url URL to access
   * @param formData `FormData` object to upload
   * @param signal Optional abort signal
   * @returns Promise
   */
  static async Upload<T>(url: string, formData: FormData, signal?: AbortSignal): Promise<T> {
    const response = await this.axios().post<T>(App.config.apiURL + url, formData, {
      signal: signal,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return response.data;
  }
}

export { Api }
